import React from 'react';
import styled from 'styled-components';

const PolicyContainer = styled.div`
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Arial', sans-serif;
  color: #ffffff;

  h1, h2, h3 {
    color: #ffffff;
    margin-bottom: 10px;
  }

  p, li {
    line-height: 1.6;
    margin-bottom: 10px;
  }

  ul {
    padding-left: 20px;
  }

  a {
    color: #add8e6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsOfService: React.FC = () => {
  return (
    <PolicyContainer>
      <h1>Terms of Service</h1>
      <p><em>Last Updated: June 3, 2026</em></p>

      <p>These Terms of Service ("Terms") govern your access to and use of the MarketNest website, client portal, social publishing tools, content services, digital marketing services, and related software or services provided by MarketNest ("MarketNest," "we," "us," or "our"). By using our services, you agree to these Terms.</p>

      <h2>1. Services</h2>
      <p>MarketNest provides digital marketing, web design, software, automation, content creation, social media, analytics, and related business services. Specific deliverables, timelines, pricing, approvals, account access, and responsibilities may be described in separate proposals, statements of work, invoices, subscriptions, or client agreements.</p>

      <h2>2. Client Responsibilities</h2>
      <p>You agree to provide accurate information, timely feedback, required approvals, and lawful access to any accounts, platforms, content, files, or systems needed to perform the services. You are responsible for ensuring that materials you provide do not violate any third-party rights, laws, platform rules, or contractual obligations.</p>

      <h2>3. Accounts and Access</h2>
      <p>Some services may require access to client portals, publishing platforms, advertising accounts, analytics tools, social media accounts, or third-party services. You are responsible for safeguarding login credentials and for activity that occurs under accounts you control. We may suspend access if we detect misuse, security risk, nonpayment, or activity that violates these Terms.</p>

      <h2>4. Payments</h2>
      <p>Fees, billing schedules, and payment terms are set out in the applicable proposal, invoice, subscription, or agreement. Unless otherwise stated, payments are due in U.S. dollars. Late or failed payments may delay delivery or result in suspension of services. Payment processing may be handled by third-party providers such as Stripe.</p>

      <h2>5. Cancellations and Refunds</h2>
      <p>Cancellation and refund terms are described in our Fulfillment Policy and any applicable client agreement. Because digital marketing and software work often involves allocated resources and work performed upfront, fees for completed or in-progress work are generally non-refundable unless otherwise agreed in writing.</p>

      <h2>6. Intellectual Property</h2>
      <p>Unless otherwise stated in a written agreement, you retain ownership of materials you provide to MarketNest. Upon full payment, final client-specific deliverables created for you are assigned or licensed to you as described in the applicable agreement. MarketNest retains ownership of pre-existing tools, templates, processes, software, know-how, reusable components, and internal systems used to provide services.</p>

      <h2>7. Acceptable Use</h2>
      <p>You agree not to use our services to create, publish, promote, or distribute unlawful, deceptive, infringing, harmful, abusive, discriminatory, or platform-prohibited content. You also agree not to interfere with our systems, attempt unauthorized access, misuse automation, or use our services in a way that could harm MarketNest, our clients, third-party platforms, or the public.</p>

      <h2>8. Third-Party Platforms</h2>
      <p>Our services may integrate with third-party platforms such as social networks, advertising networks, analytics providers, hosting services, payment processors, AI tools, and software providers. Your use of those platforms is subject to their own terms and policies. MarketNest is not responsible for outages, policy changes, account restrictions, API limits, moderation decisions, or actions taken by third-party platforms.</p>

      <h2>9. Results and No Guarantees</h2>
      <p>We work to provide high-quality strategy, execution, and support, but marketing and platform results can vary based on competition, budgets, algorithms, market conditions, client approvals, and third-party systems. We do not guarantee specific rankings, revenue, views, followers, conversions, platform approvals, or business outcomes unless expressly stated in a written agreement.</p>

      <h2>10. Confidentiality</h2>
      <p>We will take reasonable steps to protect confidential business information you provide to us. You agree to protect confidential information, workflows, credentials, pricing, software access, and non-public materials provided by MarketNest.</p>

      <h2>11. Limitation of Liability</h2>
      <p>To the fullest extent permitted by law, MarketNest will not be liable for indirect, incidental, special, consequential, punitive, or lost-profit damages arising from your use of our services. Our total liability for any claim related to the services will not exceed the amount you paid to MarketNest for the services giving rise to the claim during the three months before the event giving rise to liability.</p>

      <h2>12. Termination</h2>
      <p>We may suspend or terminate services if you violate these Terms, fail to pay amounts due, misuse our services, or create legal, security, operational, or reputational risk. Sections that by their nature should survive termination will remain in effect.</p>

      <h2>13. Changes to These Terms</h2>
      <p>We may update these Terms from time to time. Changes are effective when posted on this page. Your continued use of our services after changes are posted means you accept the updated Terms.</p>

      <h2>14. Contact Us</h2>
      <p>If you have questions about these Terms, contact us:</p>
      <ul>
        <li>By email: <a href="mailto:info@marketnest.org">info@marketnest.org</a></li>
        <li>By phone: <a href="tel:+1-919-504-1542">919-504-1542</a></li>
        <li>By visiting: <a href="/contact">https://marketnest.org/contact</a></li>
      </ul>
    </PolicyContainer>
  );
};

export default TermsOfService;
